import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { browserHistory } from 'utils/navigation'
import { MODAL_BUTTONS_MAP, MODAL_CLASSES_MAP } from 'utils/constants'
import STRINGS from 'utils/strings'
import { getFormattedDateString } from 'utils/time'
import { Button, Container, Divider, Popup, AccountSetupTooltip, Checkbox, Radio } from 'components/UIElements'
import TemporaryPassword from './TemporaryPassword'

const ModalContent = ({ isDisable, closeModal, objToBeSaved, saveToDatabase }) => {
  const [selectedOption, setSelectedOption] = useState(null)
  const [newObj, updateObj] = useState(objToBeSaved)

  const handleOptionSelect = value => {
    setSelectedOption(value)
    updateObj(prevObj => ({
      ...prevObj,
      participant: {
        ...prevObj.participant,
        metadata: {
          ...prevObj.participant.metadata,
          status: value,
        },
      },
    }))
  }

  const modalText = isDisable ? STRINGS.enableAccountDesc : STRINGS.disableAccountDesc

  const radioOptions = [
    { value: 'completed', label: 'Completed' },
    { value: 'early_terminated', label: 'Early Terminated' },
    { value: 'consent_withdrawn', label: 'Consent Withdrawn' },
    { value: 'screen_fail', label: 'Screen Fail' },
  ]

  return (
    <div className='disable-ptp-modal flexed column'>
      {!isDisable && <i className='fas fa-exclamation-circle fa-4x' />}
      <h4>
        {isDisable ? STRINGS.enable : STRINGS.disable} {name}
      </h4>
      <p>{modalText}</p>
      {!isDisable && (
        <>
          <p>Please select the last participant status before disabling the account</p>
          <div className='radio-selection flexed'>
            {radioOptions.map(option => (
              <Radio
                key={option.value}
                selected={selectedOption === option.value}
                content={option.label}
                onClick={() => handleOptionSelect(option.value)}
              />
            ))}
          </div>
        </>
      )}
      <div className='account-settings-buttons'>
        <Button
          grey
          id='modal-cancel'
          content={MODAL_BUTTONS_MAP.cancel}
          onClick={() => {
            setSelectedOption(null)
            closeModal()
          }}
        />
        <Button
          id='modal-confirm'
          disabled={!isDisable && !selectedOption}
          className={isDisable ? '' : 'orange'}
          content={isDisable ? MODAL_BUTTONS_MAP.enable : MODAL_BUTTONS_MAP.disable}
          onClick={async () => {
            await saveToDatabase(newObj)
            closeModal()
          }}
        />
      </div>
    </div>
  )
}

const AccountSettings = ({
  baseSiteID,
  canResendInviteOrResetPw,
  canWrite,
  checkedCohorts,
  checkedSites,
  closeModal,
  consentRequired,
  generateTempPassword,
  isEmailless,
  noPII,
  openModal,
  participant,
  participantDataFields,
  ptpSiteID,
  requestSending,
  saveToDatabase,
  study,
  studyID,
  submitCancelDeactivation,
  tempPwdLoading,
  fetchPtp,
  manualConsentEnabled,
  studyVersionID,
  isCaregiver = false,
}) => {
  const {
    opt_out,
    username,
    fname,
    lname,
    id,
    deactivation_request_timestamp,
    registration_timestamp,
    last_updated,
    manual_consent: manualConsent,
  } = participant
  const isPtpDisable = opt_out || false
  const name = noPII ? username : `${fname} ${lname}`

  const onToggle = () => {
    openModal({
      content: (
        <ModalContent
          closeModal={closeModal}
          isDisable={isPtpDisable}
          noPII={noPII}
          participant={participant}
          saveToDatabase={saveToDatabase}
          objToBeSaved={{
            studyID: study.id,
            // Use participant's site from view context (ptpSiteID) so PATCH never uses cached/wrong site
            siteID: ptpSiteID != null ? ptpSiteID : baseSiteID,
            participant: {
              ...participant,
              opt_out: !isPtpDisable,
            },
            checkedCohorts,
            participantID: participant.id,
            consentRequired,
            sites: checkedSites,
            pDataFields: participantDataFields,
            isNotLeaving: true,
            ptpField: 'opt_out',
          }}
        />
      ),
      className: MODAL_CLASSES_MAP.accountSettings,
      noButtons: true,
    })
  }

  const onSubmitManualConsent = async () => {
    await saveToDatabase({
      studyID: study.id,
      siteID: ptpSiteID != null ? ptpSiteID : baseSiteID,
      participant: {
        ...participant,
        manual_consent: !manualConsent,
      },
      checkedCohorts,
      participantID: participant.id,
      consentRequired,
      sites: checkedSites,
      pDataFields: participantDataFields,
      isNotLeaving: true,
    })
    await fetchPtp(study.id, ptpSiteID, participant.id)
  }

  const onRejectAccount = () => {
    openModal({
      content: `Reject account deletion request from ${name}`,
      className: MODAL_CLASSES_MAP.accountDeletion,
      confirmButton: STRINGS.confirmAndRejectRequest,
      cancelButton: MODAL_BUTTONS_MAP.cancel,
      onConfirm: () => {
        submitCancelDeactivation({ ptpId: id, studyID, siteID: ptpSiteID })
      },
    })
  }

  return (
    <>
      {canResendInviteOrResetPw ? (
        <>
          <div className='flexed'>
            <div className='flexed end-justified'>
              <div className='title account-title no-margin' style={{ fontWeight: 'bold' }}>
                {STRINGS.accountSettingsCap}
              </div>
              {isEmailless && (
                <Popup
                  className='box-popup account-setup-info'
                  align='left'
                  position='bottom'
                  hover
                  dark
                  trigger={<i className='fas fa-info-circle' />}>
                  <AccountSetupTooltip
                    url={study.config?.complete_account_setup_url}
                    fileName='complete_account_setup_url.pdf'
                  />
                </Popup>
              )}
            </div>
            <span>
              {STRINGS.profileCreated}: {getFormattedDateString(registration_timestamp)}
            </span>
          </div>
          <TemporaryPassword
            lastUpdated={last_updated}
            participant={participant}
            studyID={studyID}
            generateTempPassword={generateTempPassword}
            tempPwdLoading={tempPwdLoading}
            isEmailless={isEmailless}
            study={study}
            isCaregiver={isCaregiver}
          />
        </>
      ) : null}
      {canWrite ? (
        <Container className='account-settings'>
          <div className='account-content flexed'>
            <div className='account-body'>
              {isPtpDisable && <i className='fas fa-lock' />}
              <div className='account-text'>
                <h6>{isPtpDisable ? STRINGS.accountDisabled : STRINGS.accountEnabled}</h6>
                <p className='account-text-description'>
                  {isCaregiver ? STRINGS.accountDisabledDescForCaregiver : STRINGS.accountDisabledDesc}
                </p>
              </div>
            </div>
            <div className='cta'>
              <Button
                grey
                content={isPtpDisable ? STRINGS.enableAccount : STRINGS.disableAccount}
                onClick={onToggle}
                loading={requestSending}
                disabled={requestSending}
              />
            </div>
          </div>
          {deactivation_request_timestamp && (
            <>
              <Divider height='2px' color='#e5e3e3' />
              <div className='account-content flexed'>
                <div className='account-body'>
                  <div className='account-text'>
                    <h6>{STRINGS.accountDeletion}</h6>
                    <p>{STRINGS.accountDeletionDesc}</p>
                  </div>
                </div>
                <div className='cta'>
                  <Button
                    onClick={() => browserHistory.push(`/studies/${studyID}/${studyVersionID}/data-correction`)}
                    grey
                    id='submit-dcf'
                    content={STRINGS.submitADcf}
                  />
                  <Button onClick={onRejectAccount} className='reject' grey content='Reject Account Deletion' />
                </div>
              </div>
            </>
          )}
        </Container>
      ) : null}
      {manualConsentEnabled ? (
        <Container className='account-settings'>
          <Checkbox
            id='manual-consent'
            label={STRINGS.manualConsentLabel}
            checked={!!manualConsent}
            disabled={isPtpDisable}
            onClick={onSubmitManualConsent}
          />
        </Container>
      ) : null}
    </>
  )
}

AccountSettings.propTypes = {
  baseSiteID: PropTypes.number,
  checkedCohorts: PropTypes.object,
  checkedSites: PropTypes.object,
  closeModal: PropTypes.func,
  canResendInviteOrResetPw: PropTypes.bool,
  canWrite: PropTypes.bool,
  consentRequired: PropTypes.bool,
  generateTempPassword: PropTypes.func,
  isEmailless: PropTypes.bool,
  noPII: PropTypes.bool,
  openModal: PropTypes.func,
  participant: PropTypes.object,
  participantDataFields: PropTypes.object,
  ptpSiteID: PropTypes.number,
  requestSending: PropTypes.bool,
  saveToDatabase: PropTypes.func,
  study: PropTypes.object,
  studyID: PropTypes.number,
  submitCancelDeactivation: PropTypes.func,
  tempPwdLoading: PropTypes.bool,
  updateField: PropTypes.func,
  isCaregiver: PropTypes.bool,
}

export default AccountSettings
